import {
  Box,
  Heading,
  Text,
  VStack,
  UnorderedList,
  ListItem,
  Link,
} from '@chakra-ui/react';
import { sectionListSx } from '../../../utils/sectionListSx';

export const UserTermsPage = () => {
  return (
    <Box
      bg="white"
      color="gray.800"
      maxW="756px"
      mx="auto"
      px={6}
      pt={16}
      pb={24}
      fontFamily="marketingPage"
      fontSize="15px"
      lineHeight="24px">
      <VStack align="start" spacing={16}>
        {/* Header */}
        <Box>
          <Heading as="h1" fontSize="40px" mb={4} fontFamily="marketingPage">
            SaaS User Terms and Conditions
          </Heading>
          <Text fontSize="sm" color="gray.600">
            Effective Date: 2026-01-01
          </Text>
          <Text fontSize="sm" color="gray.600">
            Last Updated: 2026-01-01
          </Text>
        </Box>

        {/* Section 1 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            1. Background
          </Heading>
          <UnorderedList pl={4} spacing={2} sx={sectionListSx(1)}>
            <ListItem>
              <strong>Application.</strong> These user terms and conditions (
              <strong>User Terms</strong>) apply to each individual user (
              <strong>User</strong>) who accesses and uses Merkatus.io Oy’s (
              <strong>Merkatus</strong>) cloud-based marketing intelligence
              software-as-a-service offering (<strong>Service</strong>) pursuant
              to a subscription agreement between Merkatus and User’s employer
              or organisation (<strong>Customer</strong>). The Service is
              targeted only at corporate clients for professional use.
            </ListItem>
            <ListItem>
              <strong>Relationship to Customer Agreement.</strong> User’s access
              to the Service is granted based on the agreement between Customer
              and Merkatus (<strong>Customer Agreement</strong>). These User
              Terms supplement the Customer Agreement. User is responsible for
              complying with applicable laws and these User Terms. User further
              acknowledges that Customer is responsible to Merkatus for User’s
              compliance with these User Terms and the Customer Agreement.
            </ListItem>
            <ListItem>
              <strong>Acceptance.</strong> By indicating acceptance of these
              User Terms (whether by clicking “I Accept”, ticking a box, or
              otherwise confirming acceptance) or by accessing or using the
              Service, User agrees to be bound by these User Terms and accepts
              Merkatus’ Privacy Notice (
              <Link
                href="https://merkatus.io/privacy-policy"
                color="darkBlue.900"
                isExternal>
                https://merkatus.io/privacy-policy
              </Link>
              ). If User does not agree to the aforesaid, User must not access
              or use the Service.
            </ListItem>
          </UnorderedList>
        </Box>

        {/* Section 2 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            2. Grant of Access
          </Heading>
          <UnorderedList pl={4} spacing={2} sx={sectionListSx(2)}>
            <ListItem>
              <strong>Limited Right to Use.</strong> Subject to these User
              Terms, Merkatus grants User a personal, limited, non-exclusive,
              non-transferable, revocable right to access and use the Service
              solely for Customer’s internal business purposes and in accordance
              with these User Terms and Customer Agreement.
            </ListItem>
            <ListItem>
              <strong>User Seats.</strong> User’s access is subject to the
              number of user seats purchased by Customer. Merkatus may suspend
              or terminate User’s access if the number of active Users exceeds
              the permitted number.
            </ListItem>
          </UnorderedList>
        </Box>

        {/* Section 3 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            3. User Account and Credentials
          </Heading>
          <UnorderedList pl={4} spacing={2} sx={sectionListSx(3)}>
            <ListItem>
              <strong>Personal Account.</strong> Each User account, including
              the username and password, is personal to the individual User.
              User shall not share, transfer or disclose User’s username,
              password or account credentials to any third party.
            </ListItem>
            <ListItem>
              <strong>Account Security.</strong> User is responsible for
              maintaining the confidentiality and security of User’s account
              credentials. User shall immediately notify Customer and Merkatus
              of any unauthorised use of User’s account or any other security
              breach.
            </ListItem>
            <ListItem>
              <strong>Accurate Information.</strong> User shall provide
              accurate, current and complete information when creating an
              account and shall promptly update such information to maintain its
              accuracy.
            </ListItem>
          </UnorderedList>
        </Box>

        {/* Section 4 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            4. Permitted Use and Restrictions
          </Heading>
          <UnorderedList pl={4} spacing={2} sx={sectionListSx(4)}>
            <ListItem>
              <strong>Compliance.</strong> User shall use the Service only in
              accordance with: (a) these User Terms and the Customer Agreement;
              (b) applicable laws and regulations; and (c) any instructions or
              policies provided by Customer or Merkatus.
            </ListItem>
            <ListItem>
              <strong>Prohibited Activities.</strong> User shall not: (a)
              interfere with or disrupt the integrity or performance of the
              Service; (b) attempt to gain unauthorised access to the Service or
              its related systems or networks; (c) remove, alter or obscure any
              proprietary notices on the Service; (d) use the Service to
              transmit any viruses, malware or other harmful code; or (e) use
              the Service in any manner that could damage, disable or impair the
              Service.
            </ListItem>
          </UnorderedList>
        </Box>

        {/* Section 5 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            5. Intellectual Property
          </Heading>
          <UnorderedList pl={4} spacing={2} sx={sectionListSx(5)}>
            <ListItem>
              <strong>Merkatus’ Rights.</strong> User acknowledges and agrees
              that Merkatus and its licensors hold all rights, title and
              interest in and to the Service, including all intellectual
              property rights therein. All rights not expressly granted to User
              are reserved by Merkatus and its licensors.
            </ListItem>
            <ListItem>
              <strong>Restrictions.</strong> User shall not acquire any rights,
              title or interest in the Service, related deliverables or outputs
              other than the limited right to use the Service as expressly set
              out in these User Terms. The Service and underlying software
              contain business secrets and proprietary information of Merkatus
              and third parties. Any copying, modification, translation,
              decompiling, disassembling or reverse engineering of the Service
              is strictly prohibited.
            </ListItem>
            <ListItem>
              <strong>Customer Data.</strong> As between User and Merkatus, all
              data, content and materials uploaded, submitted or created by User
              in the Service (<strong>User Content</strong>) belongs to
              Customer. User grants Merkatus the right to use, process and store
              User Content to the extent necessary to provide the Service to
              Customer and User in accordance with the Customer Agreement.
              Further, Merkatus may collect and use aggregated usage data and
              statistics derived from User’s use of the Service in anonymised
              form, provided that such data cannot be used to identify Customer,
              User or any individual. Merkatus may use such anonymised data for
              product development, benchmarking, analytics and research purposes
              and may share such data with third-party service providers for
              these purposes.
            </ListItem>
          </UnorderedList>
        </Box>

        {/* Section 6 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            6. Data Protection and Privacy
          </Heading>
          <UnorderedList pl={4} spacing={2} sx={sectionListSx(6)}>
            <ListItem>
              <strong>Personal Data.</strong> User acknowledges and agrees that
              User’s personal data (including account information, contact
              details and location data) may be processed by Merkatus in
              accordance with Merkatus’ Privacy Notice and applicable data
              protection laws.
            </ListItem>
            <ListItem>
              <strong>User Responsibilities.</strong> User is responsible for
              ensuring that any personal data or other data uploaded to the
              Service by User: (a) is uploaded lawfully and in compliance with
              applicable data protection laws; (b) does not infringe any
              third-party rights; and (c) complies with these User Terms.
            </ListItem>
            <ListItem>
              <strong>Data Controller.</strong> User acknowledges that for User
              account and authentication data, Merkatus is the data controller.
              For any personal data that User submits to the Service in the
              course of using the Service, Customer is the data controller and
              Merkatus is the data processor.
            </ListItem>
          </UnorderedList>
        </Box>

        {/* Section 7 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            7. Term
          </Heading>
          <UnorderedList pl={4} spacing={2} sx={sectionListSx(7)}>
            <ListItem>
              <strong>Term.</strong> These User Terms commence when User first
              accepts them or first accesses the Service (whichever is earlier)
              and continue until User’s access to the Service is terminated.
            </ListItem>
            <ListItem>
              <strong>Termination by Customer.</strong> Customer may terminate
              User’s access to the Service at any time. User acknowledges that
              Merkatus has no obligation to maintain User’s access if Customer
              requests termination or if the Customer Agreement terminates.
            </ListItem>
            <ListItem>
              <strong>Termination by Merkatus.</strong> Merkatus may suspend or
              terminate User’s access to the Service immediately if: (a) User
              breaches these User Terms; (b) the Customer Agreement terminates
              or expires; (c) Customer requests termination of User’s access;
              (d) User’s actions pose a security risk to the Service or other
              users; or (e) required by law or regulatory authorities.
            </ListItem>
            <ListItem>
              <strong>Effect of Termination.</strong> Upon termination of User’s
              access, User shall immediately cease all use of the Service. User
              acknowledges that User has no right to access User Content after
              termination, and any such access is subject to the Customer
              Agreement between Customer and Merkatus.
            </ListItem>
          </UnorderedList>
        </Box>

        {/* Section 8 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            8. Limitation of Liability
          </Heading>
          <UnorderedList pl={4} spacing={2} sx={sectionListSx(8)}>
            <ListItem>
              <strong>No Warranties.</strong> The Service is provided to User
              “as is” and “as available” without warranties of any kind. User
              uses the Service at User’s own risk.
            </ListItem>
            <ListItem>
              <strong>Limitation.</strong> To the maximum extent permitted by
              law, Merkatus shall not be liable to User for any direct,
              indirect, incidental, consequential or special damages arising out
              of or related to the Service or use thereof.
            </ListItem>
            <ListItem>
              <strong>Customer Relationship.</strong> User acknowledges that
              User’s primary relationship is with Customer. Any claims relating
              to User’s access to or use of the Service should be directed to
              Customer in the first instance.
            </ListItem>
          </UnorderedList>
        </Box>

        {/* Section 9 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            9. Miscellaneous
          </Heading>
          <UnorderedList pl={4} spacing={2} sx={sectionListSx(9)}>
            <ListItem>
              <strong>Governing Law.</strong> These User Terms shall be governed
              by the laws of Finland, excluding its choice of law provisions.
            </ListItem>
            <ListItem>
              <strong>Modifications.</strong> Merkatus may modify these User
              Terms from time to time by providing notice through the Service or
              by email. Continued use of the Service after such notice
              constitutes User’s acceptance of the modified User Terms.
            </ListItem>
            <ListItem>
              <strong>Severability.</strong> If any provision of these User
              Terms is held to be invalid or unenforceable, the remaining
              provisions shall remain in full force and effect.
            </ListItem>
            <ListItem>
              <strong>Entire Agreement.</strong> These User Terms, together with
              the Privacy Notice and the Customer Agreement, constitute the
              entire agreement between User and Merkatus regarding User’s use of
              the Service.
            </ListItem>
          </UnorderedList>
        </Box>

        {/* Section 12 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            10. Contact
          </Heading>
          <Text>
            Merkatus.io Oy
            <br />
            Lapinlahdenkatu 16, 00180 Helsinki, Finland
            <br />
            <Link
              href="mailto:support@merkatus.io"
              color="darkBlue.900"
              fontWeight="semibold">
              support@merkatus.io
            </Link>
          </Text>
        </Box>
      </VStack>
    </Box>
  );
};
